import { compact, groupBy, mapValues, orderBy } from 'lodash';

import PRODUCTS, {
  LOG_PRODUCTS,
  PRODUCT_SUB_TYPES
} from '@/constants/products.constants';
import type { ProductType } from '@/pages/Organizations/Organizations.types';

import {
  ALL_TILES,
  INGEST_CASCADE,
  LAST_ACTION_FAILED_LABEL,
  MICROSOFT_DEVICE_ACTION_TYPES,
  MUTUALLY_EXCLUSIVE_APP_CATEGORIES,
  PREREQUISITE_MISSING_LABEL,
  TOKEN_STATUS_LABEL
} from './MsDefenderIntegration.constants';
import type {
  AggregateConsentStatus,
  AppType,
  ConsentEligibility,
  ConsentStatus,
  ConsentTileConfig,
  MicrosoftIntegration,
  MicrosoftIntegrationsByAppType,
  ResponseActionHealthStats,
  ResponseActionSummaryDTO,
  TenantCategoryStateById
} from './MsDefenderIntegration.types';
import {
  AppType as AppTypeEnum,
  ConsentStatus as ConsentStatusEnum,
  ResponseActionStatus
} from './MsDefenderIntegration.types';

export const getUiStatus = (
  integration: MicrosoftIntegration | undefined
): ConsentStatus =>
  integration?.integration_data.api_token_status ?? ConsentStatusEnum.CONNECTED;

export const groupIntegrationsByAppType = (
  integrations: MicrosoftIntegration[] | undefined
): MicrosoftIntegrationsByAppType => {
  const integrationsByAppType = groupBy(
    integrations ?? [],
    (integration) => integration.integration_data.app_type
  );

  return mapValues(integrationsByAppType, (appIntegrations) =>
    orderBy(
      appIntegrations,
      [(integration) => integration.tenant_id ?? '', 'id'],
      ['asc', 'asc']
    )
  ) as MicrosoftIntegrationsByAppType;
};

export const getCategoryStateByTenant = (
  integrations: MicrosoftIntegration[] | undefined,
  appType: AppType
): TenantCategoryStateById => {
  const categoryAppTypes = MUTUALLY_EXCLUSIVE_APP_CATEGORIES.find((category) =>
    category.includes(appType)
  );

  if (!categoryAppTypes) return {};

  const categoryIntegrations = (integrations ?? []).filter(
    (integration) =>
      integration.tenant_id &&
      categoryAppTypes.includes(integration.integration_data.app_type)
  );

  return mapValues(
    groupBy(categoryIntegrations, 'tenant_id'),
    (tenantRows) => ({
      tenantId: tenantRows[0].tenant_id ?? '',
      integrations: orderBy(tenantRows, ['id'], ['asc']),
      hasConflict:
        new Set(tenantRows.map((row) => row.integration_data.app_type)).size > 1
    })
  );
};

export const hasConnectedDefenderIngestion = (
  integrations: MicrosoftIntegration[] | undefined,
  tenantId?: string | null
): boolean =>
  (integrations ?? []).some(
    (integration) =>
      integration.tenant_id === tenantId &&
      integration.integration_data.app_type ===
        AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION &&
      getUiStatus(integration) === ConsentStatusEnum.CONNECTED
  );

export const getStatusChipColor = (
  status: ConsentStatus
): 'success' | 'default' | 'warning' | 'error' => {
  switch (status) {
    case ConsentStatusEnum.CONNECTED:
      return 'success';
    case ConsentStatusEnum.ERROR:
      return 'error';
    case ConsentStatusEnum.INSUFFICIENT_PERMISSIONS:
      return 'warning';
    default:
      return 'default';
  }
};

export const getCombinedTenantStatus = (
  tokenStatus: ConsentStatus,
  actionStatus?: ConsentStatus
): AggregateConsentStatus => {
  if (tokenStatus !== ConsentStatusEnum.CONNECTED) {
    return { status: tokenStatus, label: TOKEN_STATUS_LABEL[tokenStatus] };
  }

  if (actionStatus === ConsentStatusEnum.INSUFFICIENT_PERMISSIONS) {
    return {
      status: actionStatus,
      label: TOKEN_STATUS_LABEL[ConsentStatusEnum.INSUFFICIENT_PERMISSIONS]
    };
  }

  if (actionStatus === ConsentStatusEnum.ERROR) {
    return { status: actionStatus, label: LAST_ACTION_FAILED_LABEL };
  }

  return {
    status: ConsentStatusEnum.CONNECTED,
    label: TOKEN_STATUS_LABEL[ConsentStatusEnum.CONNECTED]
  };
};

export const hasMicrosoftLogProduct = (
  activeProducts: ProductType[]
): boolean =>
  activeProducts.some((product) =>
    LOG_PRODUCTS.includes(product.product_type as PRODUCTS)
  );

export const hasMicrosoftEdrMsdProduct = (
  activeProducts: ProductType[]
): boolean =>
  activeProducts.some(
    (product) =>
      product.product_type === PRODUCTS.EDR &&
      product.sub_type === PRODUCT_SUB_TYPES.MSD
  );

export const getConsentEligibility = (
  _tile: ConsentTileConfig,
  activeProducts: ProductType[]
): ConsentEligibility => {
  return {
    isEligible: hasMicrosoftLogProduct(activeProducts),
    requirement:
      'Microsoft integration requires an active LOG, LOG_USER, or LOG_KANATI product.'
  };
};

export const shouldShowMicrosoftIntegration = (
  activeProducts: ProductType[]
): boolean => hasMicrosoftLogProduct(activeProducts);

export const getCascadeTitles = (
  disconnectTarget: ConsentTileConfig | null
): string[] => {
  const cascadeDependencies: AppType[] | undefined = disconnectTarget
    ? INGEST_CASCADE[disconnectTarget.appType]
    : undefined;

  return compact(
    cascadeDependencies?.map(
      (dependencyType) =>
        ALL_TILES.find((tile) => tile.appType === dependencyType)?.title
    ) ?? []
  );
};

export const getDisconnectDescription = (cascadeTitles: string[]): string =>
  cascadeTitles.length > 0
    ? 'This permanently disconnects the Microsoft app for this tenant and any dependent connections for the same tenant. You will need to re-consent to reconnect.'
    : 'This permanently disconnects the Microsoft app for this tenant. You will need to re-consent to reconnect.';

export const calculateResponseActionHealth = (
  responseActions: ResponseActionSummaryDTO[]
): ResponseActionHealthStats => {
  let succeeded = 0;
  let failed = 0;
  let lastSuccessDate: string | null = null;

  for (const action of responseActions) {
    if (
      action.current_status.toLowerCase() === ResponseActionStatus.COMPLETED
    ) {
      succeeded += 1;
      if (!lastSuccessDate) {
        lastSuccessDate = action.created_at;
      }
    } else {
      failed += 1;
    }
  }

  return { total: responseActions.length, succeeded, failed, lastSuccessDate };
};

export const getLatestFailedAction = (
  responseActions: ResponseActionSummaryDTO[]
): ResponseActionSummaryDTO | null => {
  if (responseActions.length === 0) {
    return null;
  }
  const latest = responseActions[0];
  return latest.current_status.toLowerCase() !== ResponseActionStatus.COMPLETED
    ? latest
    : null;
};

export const getSkippedResponseActionsCount = (
  responseActions: ResponseActionSummaryDTO[]
): number => {
  if (responseActions.length === 0) {
    return 0;
  }
  if (
    responseActions[0].current_status.toLowerCase() !==
    ResponseActionStatus.SKIPPED
  ) {
    return 0;
  }
  return responseActions.filter(
    (action) =>
      action.current_status.toLowerCase() === ResponseActionStatus.SKIPPED
  ).length;
};

export const getResponseActionRowStatus = (
  tokenStatus: ConsentStatus,
  isLegacyInvalid: boolean,
  responseActions: ResponseActionSummaryDTO[]
): AggregateConsentStatus => {
  if (isLegacyInvalid) {
    return {
      status: ConsentStatusEnum.ERROR,
      label: PREREQUISITE_MISSING_LABEL
    };
  }
  if (tokenStatus === ConsentStatusEnum.ERROR) {
    return {
      status: ConsentStatusEnum.ERROR,
      label: TOKEN_STATUS_LABEL[ConsentStatusEnum.ERROR]
    };
  }
  if (tokenStatus === ConsentStatusEnum.INSUFFICIENT_PERMISSIONS) {
    return {
      status: ConsentStatusEnum.INSUFFICIENT_PERMISSIONS,
      label: TOKEN_STATUS_LABEL[ConsentStatusEnum.INSUFFICIENT_PERMISSIONS]
    };
  }
  if (responseActions.length === 0) {
    return {
      status: ConsentStatusEnum.CONNECTED,
      label: TOKEN_STATUS_LABEL[ConsentStatusEnum.CONNECTED]
    };
  }

  const latest = responseActions[0];
  if (latest.current_status.toLowerCase() === ResponseActionStatus.ERROR) {
    return { status: ConsentStatusEnum.ERROR, label: 'Error' };
  }

  return {
    status: ConsentStatusEnum.CONNECTED,
    label: TOKEN_STATUS_LABEL[ConsentStatusEnum.CONNECTED]
  };
};

export const formatResponseActionSuccessDate = (
  dateString: string | null
): string => {
  if (!dateString) {
    return 'None';
  }
  try {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      }) +
      ', ' +
      date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    );
  } catch {
    return dateString;
  }
};

export const isDeviceResponseAction = (actionType: string): boolean =>
  MICROSOFT_DEVICE_ACTION_TYPES.has(actionType.toLowerCase());

export const formatResponseActionTitle = (actionType: string): string => {
  if (isDeviceResponseAction(actionType)) {
    return 'Device response action could not be completed';
  }
  return 'Identity response action could not be completed';
};

export const formatResponseActionDescription = (
  action: ResponseActionSummaryDTO | null
): string => {
  if (!action) return '';
  if (action.detail) return action.detail;
  const subject = isDeviceResponseAction(action.action_type)
    ? 'Device'
    : 'User';
  const targetText = action.target ? `${subject} ${action.target} ` : '';
  return `${targetText}was not completed successfully.`;
};
